import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlagIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { JSONContent } from '@tiptap/core';
import cn from 'classnames';

import { COMMUNITY_ROUTES } from '~/constants/community';
import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import useToast, { type ToastData, ToastStatus } from '~/hooks/useToast';
import type { CommunityComment } from '~/interfaces/community/comment';
import { TiptapRenderer } from '~/routes/community/_components/TiptapRenderer';
import type { ActionMenuItem } from '~/routes/community/_components/ui/ActionMenu';
import { ActionMenu } from '~/routes/community/_components/ui/ActionMenu';
import { Avatar } from '~/routes/community/_components/ui/Avatar';
import { ConfirmDialog } from '~/routes/community/_components/ui/ConfirmDialog';
import { EditedIndicator } from '~/routes/community/_components/ui/EditedIndicator';
import { MemberBadge } from '~/routes/community/_components/ui/MemberBadge';
import { MemberProfileLink } from '~/routes/community/_components/ui/MemberProfileLink';
import { ReportReasonFields } from '~/routes/community/_components/ui/ReportReasonFields';
import { useBlockMemberMutation } from '~/routes/community/_hooks/useBlockMemberMutation';
import { useCommunityMembership } from '~/routes/community/_hooks/useCommunityMembership';
import { useMemberBlockingEnabled } from '~/routes/community/_hooks/useMemberBlockingEnabled';
import { useMemberModeration } from '~/routes/community/_hooks/useMemberModeration';
import useReportCommentMutation from '~/routes/community/_hooks/useReportCommentMutation';
import { useReportToBeehiiv } from '~/routes/community/_hooks/useReportToBeehiiv';
import { useShortRelativeDate } from '~/routes/community/_hooks/useShortRelativeDate';
import { getActionErrorMessage } from '~/routes/community/_utils/getActionErrorMessage';
import { isCommunityComment } from '~/routes/community/_utils/isCommunityComment';
import { isAuthorProtectedFromBan, isAuthorProtectedFromUnban } from '~/routes/community/_utils/permissions';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import { withNectarTrackingParams } from '~/utils/nectarTracking';
import toast from '~/utils/toast';

import { CommentActions } from './CommentActions';
import { CommentComposer } from './CommentComposer';

// px-4 + avatar (size-10) + gap-4 — aligns body text and reactions with the author name
const COMMENT_BODY_INSET_CLASS = 'pl-[72px]';
const COMMENT_ACTIONS_INSET_CLASS = 'pl-16';

// Must match Communities::PostComments::Update::EDIT_WINDOW in the backend
const EDIT_WINDOW_MS = 15 * 60 * 1000;
const MAX_REPLY_DEPTH = 1;

interface CommentProps {
  comment: CommunityComment;
  postId: string;
  commentsState: 'open' | 'closed' | 'hidden';
  isRemoved?: boolean;
  isReply?: boolean;
  hasReplies?: boolean;
  highlighted?: boolean;
  onReply?: (comment: CommunityComment) => void;
  onDelete?: (options?: { reason?: string }) => boolean | Promise<boolean>;
  onUpdate?: (comment: CommunityComment) => void;
  onCommentCreated?: () => void;
  spaceSlug?: string;
}

export const Comment: FC<CommentProps> = ({
  comment,
  postId,
  commentsState,
  isRemoved,
  isReply = false,
  hasReplies = false,
  highlighted = false,
  onReply,
  onDelete,
  onUpdate,
  onCommentCreated,
  spaceSlug,
}) => {
  const { t } = useTranslation();
  const formatRelativeTime = useShortRelativeDate();
  const { currentMember, permissions } = useCommunityMembership();
  const replyFetcher = useNonRevalidatingFetcher();
  const reportMutation = useReportCommentMutation();
  const { mutate: blockMember } = useBlockMemberMutation();
  const memberBlockingEnabled = useMemberBlockingEnabled();
  const [showReplyComposer, setShowReplyComposer] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [removalReason, setRemovalReason] = useState('');
  const [isRemoveSubmitting, setIsRemoveSubmitting] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportToBeehiiv, setReportToBeehiiv] = useState(false);
  const [blockUser, setBlockUser] = useState(false);
  const escalateToBeehiiv = useReportToBeehiiv();
  const [toastData, setToastData] = useState<ToastData>();
  useToast(toastData);

  const isOwnComment = currentMember?.id === comment.author.id;
  const isModeratorRemoval = !isOwnComment && permissions.canRemoveAnyComment;
  const canReply = comment.depth_level <= MAX_REPLY_DEPTH && permissions.canComment && commentsState === 'open';
  const isWithinEditWindow = Date.now() - new Date(comment.created_at).getTime() < EDIT_WINDOW_MS;
  const wasEdited = comment.updated_at !== comment.created_at;

  const authorIsBanned = comment.author.computed?.banned === true;
  const authorIsBlocked = comment.author.computed?.blocking === true;
  const authorProtectedFromBan = isAuthorProtectedFromBan(permissions.isManager, comment.author.roles);
  const authorProtectedFromUnban = isAuthorProtectedFromUnban(permissions.isManager, comment.author.roles);
  const canActOnAuthor = !!currentMember && !isOwnComment && !permissions.isRestricted;

  const { menuItems: moderationMenuItems } = useMemberModeration({
    memberId: comment.author.id,
    memberName: comment.author.name ?? '',
    canBanAuthor: permissions.canBanMember && !isOwnComment && !authorIsBanned && !authorProtectedFromBan,
    canUnbanAuthor: permissions.canBanMember && !isOwnComment && authorIsBanned && !authorProtectedFromUnban,
    canBlockAuthor: canActOnAuthor && !authorIsBlocked,
    canUnblockAuthor: canActOnAuthor && authorIsBlocked,
  });

  const displayName = comment.author.name || t('community.join.former_member');

  const menuItems: ActionMenuItem[] = [];

  if (isOwnComment && isWithinEditWindow && permissions.canInteract) {
    menuItems.push({
      key: 'edit',
      label: t('community.comments.edit'),
      icon: PencilSquareIcon,
      onClick: () => setIsEditing(true),
    });
  }

  if ((isOwnComment || permissions.canRemoveAnyComment) && permissions.canInteract) {
    menuItems.push({
      key: 'delete',
      label: isOwnComment ? t('community.comments.delete') : t('community.comments.remove'),
      icon: TrashIcon,
      onClick: () => {
        if (isModeratorRemoval) {
          setIsRemoveDialogOpen(true);
          return;
        }
        void onDelete?.();
      },
      variant: 'danger',
    });
  }

  const canReport = !!currentMember && !isOwnComment && permissions.canInteract;
  if (canReport) {
    menuItems.push({
      key: 'report',
      label: t('community.action_menu.report_comment'),
      icon: FlagIcon,
      onClick: () => setIsReportOpen(true),
    });
  }

  if (moderationMenuItems.length > 0 && menuItems.length > 0) {
    menuItems.push({ type: 'divider', key: 'divider-moderation' });
  }
  menuItems.push(...moderationMenuItems);

  const handleConfirmReport = useCallback(() => {
    const trimmedReason = reportReason.trim();
    reportMutation.mutate(
      { comment_id: comment.id, reason: trimmedReason },
      {
        onSuccess: (data) => {
          setToastData(data.toast);
          if (blockUser) blockMember({ memberId: comment.author.id, block: true });
          setIsReportOpen(false);
          setReportReason('');
          setReportToBeehiiv(false);
          setBlockUser(false);
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : t('community.action_menu.report_comment_error');
          setToastData({ status: ToastStatus.ERROR, message });
        },
      },
    );
  }, [blockMember, blockUser, comment.author.id, comment.id, reportMutation.mutate, reportReason, t]);

  const handleCancelReport = useCallback(() => {
    setIsReportOpen(false);
    setReportReason('');
    setReportToBeehiiv(false);
    setBlockUser(false);
  }, []);

  const handleReplySubmit = useCallback(
    async (json: JSONContent) => {
      const formData = new FormData();
      formData.append('post_id', postId);
      formData.append('parent_comment_id', comment.id);
      formData.append('content_json', JSON.stringify(json));
      withNectarTrackingParams(formData, NectarPageBlock.CommunityPostCommentForm);

      const response = await replyFetcher.submit(formData, { method: 'POST', action: '/community_comment' });
      if (!response.ok) {
        toast.error(getActionErrorMessage(response.data) ?? t('community.comments.submit_error'));
        return;
      }
      if (!isCommunityComment(response.data)) {
        console.error('Unexpected comment response shape', response.data);
        return;
      }

      onReply?.(response.data);
      onCommentCreated?.();
      setShowReplyComposer(false);
    },
    [comment.id, onReply, postId, replyFetcher, onCommentCreated, t],
  );

  const handleEditSubmit = useCallback(
    (json: JSONContent) => {
      onUpdate?.({
        ...comment,
        tiptap_state: json,
        updated_at: new Date().toISOString(),
      });
      setIsEditing(false);
    },
    [comment, onUpdate],
  );

  const handleCancelRemove = useCallback(() => {
    setIsRemoveDialogOpen(false);
    setRemovalReason('');
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    const reason = removalReason.trim();
    setIsRemoveSubmitting(true);
    try {
      const success = await onDelete?.(reason ? { reason } : undefined);
      if (success !== false) {
        setIsRemoveDialogOpen(false);
        setRemovalReason('');
      }
    } finally {
      setIsRemoveSubmitting(false);
    }
  }, [onDelete, removalReason]);

  // TODO(WEB-8312): Handle removed-comment placeholders for depth > 2 when shared comments are added
  if (isRemoved) {
    return (
      <div className="flex items-start gap-4 px-4 pt-4">
        <div className="shrink-0 size-10" />
        <p className="text-[16px] leading-6 text-black/40 italic">{t('community.comments.deleted_placeholder')}</p>
      </div>
    );
  }

  return (
    <>
      <div id={comment.id} className={cn(highlighted && 'mx-2 rounded-2xl bg-community-primary/10')}>
        <div className="flex items-center gap-6 px-4 pt-4">
          <div className="flex flex-1 items-center gap-4 min-w-0">
            <MemberProfileLink memberId={comment.author.id} aria-label={displayName}>
              <Avatar src={comment.author.avatar_url} alt={displayName} className="size-10" />
            </MemberProfileLink>
            <div className="flex items-center gap-[6px] min-w-0">
              <MemberProfileLink
                memberId={comment.author.id}
                className="font-semibold leading-5 text-black hover:underline"
              >
                {displayName}
              </MemberProfileLink>
              <MemberBadge
                roles={comment.author.roles}
                isBanned={authorIsBanned}
                blockedYou={comment.author.computed?.blocked_you === true}
                badges={comment.author.badges}
              />
              <span className="font-normal leading-6 text-black/50">&bull;</span>
              <span className="font-normal leading-5 text-black/50 whitespace-nowrap">
                {formatRelativeTime(comment.created_at)}
              </span>
            </div>
          </div>
          {menuItems.length > 0 && <ActionMenu items={menuItems} />}
        </div>

        <div className="flex items-start">
          {!isReply && (
            <div className="relative ml-4 w-10 shrink-0 self-stretch overflow-clip">
              {comment.depth_level <= MAX_REPLY_DEPTH && hasReplies && (
                <div className="absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2 bg-black/10" />
              )}
            </div>
          )}

          <div className={cn('min-w-0 flex-1 pr-4', isReply ? undefined : 'pb-2 pl-2')}>
            <div className={cn('text-[16px] leading-6 text-black', isReply ? COMMENT_BODY_INSET_CLASS : 'pl-2')}>
              {isEditing ? (
                <CommentComposer
                  variant="inline"
                  initialContent={comment.tiptap_state}
                  onSubmit={handleEditSubmit}
                  onCancel={() => setIsEditing(false)}
                  isSubmitting={false}
                  spaceSlug={spaceSlug}
                />
              ) : (
                <TiptapRenderer tiptapState={comment.tiptap_state} />
              )}
            </div>

            {wasEdited && !isEditing && (
              <div className={cn('mt-1', isReply ? COMMENT_BODY_INSET_CLASS : 'pl-2')}>
                <EditedIndicator editedAt={comment.updated_at} />
              </div>
            )}

            <div className={cn(isReply && COMMENT_ACTIONS_INSET_CLASS, { hidden: isEditing })}>
              <CommentActions
                comment={comment}
                canReply={canReply}
                onToggleReply={() => setShowReplyComposer((prev) => !prev)}
              />
            </div>

            {showReplyComposer && (
              <div className="mt-2">
                <CommentComposer
                  variant="inline"
                  placeholder={t('community.comments.write_reply')}
                  onSubmit={handleReplySubmit}
                  onCancel={() => setShowReplyComposer(false)}
                  isSubmitting={replyFetcher.state === 'submitting'}
                  spaceSlug={spaceSlug}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={isRemoveDialogOpen}
        title={t('community.comments.remove_confirm_title')}
        description={t('community.comments.remove_confirm_description')}
        confirmLabel={t('community.comments.remove')}
        onConfirm={() => void handleConfirmRemove()}
        onCancel={handleCancelRemove}
        confirmDisabled={removalReason.trim().length === 0}
        isDestructive
        isLoading={isRemoveSubmitting}
        body={
          <div className="mt-4">
            <label htmlFor={`remove-comment-reason-${comment.id}`} className="block text-sm font-medium text-black">
              {t('community.comments.remove_reason_label')}
            </label>
            <textarea
              id={`remove-comment-reason-${comment.id}`}
              rows={3}
              maxLength={2000}
              value={removalReason}
              onChange={(event) => setRemovalReason(event.target.value)}
              placeholder={t('community.comments.remove_reason_placeholder')}
              disabled={isRemoveSubmitting}
              required
              className="mt-2 w-full rounded-2xl border border-black/10 px-3 py-2 text-sm text-black placeholder:text-black/40 focus:outline-none focus:ring-2 focus:ring-black/10 disabled:opacity-50"
            />
          </div>
        }
      />
      <ConfirmDialog
        isOpen={isReportOpen}
        title={t('community.action_menu.report_comment_confirm_title')}
        description={t('community.action_menu.report_comment_confirm_description')}
        confirmLabel={
          reportToBeehiiv
            ? t('community.report.escalate_link', 'Report to beehiiv')
            : t('community.action_menu.report_comment_confirm_button')
        }
        onConfirm={
          reportToBeehiiv
            ? () => {
                escalateToBeehiiv(reportReason, COMMUNITY_ROUTES.post(postId));
                if (blockUser) blockMember({ memberId: comment.author.id, block: true });
                handleCancelReport();
              }
            : handleConfirmReport
        }
        onCancel={handleCancelReport}
        confirmDisabled={reportReason.trim().length === 0}
        isLoading={reportMutation.isPending}
        body={
          <ReportReasonFields
            id={`report-comment-reason-${comment.id}`}
            label={t('community.action_menu.report_comment_reason_label')}
            placeholder={t('community.action_menu.report_comment_reason_placeholder')}
            value={reportReason}
            onChange={setReportReason}
            reportToBeehiiv={reportToBeehiiv}
            onReportToBeehiivChange={setReportToBeehiiv}
            showActions={memberBlockingEnabled && !authorIsBlocked}
            blockUser={blockUser}
            onBlockUserChange={setBlockUser}
          />
        }
      />
    </>
  );
};
